import { useEffect, useState } from 'react'
import Compressor from 'compressorjs'
import { CubeIcon, PhotoIcon, RectangleGroupIcon, SwatchIcon } from '@heroicons/react/24/outline'
import { Controller } from 'libs/form'
import type { FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { ColorPicker, FileUploader, FormSection, Select } from 'shared/components'
import { InputGroup } from 'shared/molecules'
import { DEFAULT_COLOR } from 'features/areaTypes/areaTypes.config'

type AreaTypeFormSectionsProps = {
	form: UseFormReturn<FieldValues>
}

export const AreaTypeFormSections = ({ form }: AreaTypeFormSectionsProps) => {
	const { t } = useTranslation()
	const [images, setImages] = useState<any[]>([])
	const {
		register,
		control,
		setValue,
		watch,
		formState: { errors }
	} = form
	const image = watch('image')
	const fieldErrors = errors as any

	useEffect(() => {
		if (!image) setImages([])
	}, [image])

	const handleImageChange = (data: any[]) => {
		if (!data.length) {
			setValue('image', null)
			return
		}

		new Compressor(data[0].file, {
			quality: 0.6,
			maxHeight: 800,
			maxWidth: 800,
			success: (compressedResult) => setValue('image', compressedResult)
		})
	}

	const algorithmOptions = [
		{ value: 'any_available', label: t('Any available date') },
		{ value: 'no_gap', label: t('No gap (single next date)') }
	]
	const dimensions = [
		{ name: 'height', label: t('Height (cm)') },
		{ name: 'width', label: t('Width (cm)') },
		{ name: 'depth', label: t('Depth (cm)') }
	]
	const colors = [
		{ name: 'primary_color', label: t('Primary color') },
		{ name: 'secondary_color', label: t('Secondary color') }
	]

	return (
		<>
			<FormSection
				title={t('Details')}
				description={t('How the area type is named and booked.')}
				icon={<RectangleGroupIcon />}>
				<InputGroup
					id="name"
					className="sm:col-span-2"
					label={t('Name')}
					placeholder={t('Name')}
					required
					{...register('name')}
					error={fieldErrors.name}
				/>
				<InputGroup
					id="description"
					className="sm:col-span-2"
					label={t('Description')}
					placeholder={t('Description')}
					{...register('description')}
					error={fieldErrors.description}
				/>
				<Controller
					name="availability_algorithm"
					control={control}
					render={({ field }) => (
						<Select
							id="availability_algorithm"
							variant="form"
							className="sm:col-span-2"
							label={t('Availability algorithm')}
							required
							isClearable={false}
							options={algorithmOptions}
							value={algorithmOptions.find((option) => option.value === field.value) ?? null}
							error={fieldErrors.availability_algorithm}
							onChange={(option: any) => field.onChange(option?.value)}
						/>
					)}
				/>
			</FormSection>

			<FormSection
				title={t('Image')}
				description={t('Shown on the area type card in the online booking widget.')}
				icon={<PhotoIcon />}>
				<div className="sm:col-span-2">
					<Controller
						name="image"
						control={control}
						render={({ field }) => (
							<FileUploader
								{...field}
								images={images}
								setImages={setImages}
								onChange={handleImageChange}
							/>
						)}
					/>
				</div>
			</FormSection>

			<FormSection
				title={t('Dimensions')}
				description={t('Size of one area of this type.')}
				icon={<CubeIcon />}
				tone="success"
				className="sm:grid-cols-3">
				{dimensions.map(({ name, label }) => (
					<InputGroup
						key={name}
						id={name}
						type="number"
						label={label}
						placeholder="0"
						required
						{...register(`dimensions.${name}`)}
						error={fieldErrors.dimensions?.[name]}
					/>
				))}
			</FormSection>

			<FormSection
				title={t('Colors')}
				description={t('Colors that represent this area type.')}
				icon={<SwatchIcon />}
				tone="warning">
				{colors.map(({ name, label }) => (
					<Controller
						key={name}
						name={name}
						control={control}
						render={({ field }) => (
							<ColorPicker
								id={name}
								label={label}
								color={field.value ?? DEFAULT_COLOR}
								onChange={({ hex }) => field.onChange(hex)}
							/>
						)}
					/>
				))}
			</FormSection>
		</>
	)
}
